import { Skeleton } from "@mui/material";
import { DDD, E2Data } from "../../lib/ddd/ddd";
import classes from "./DashboardComponents.module.scss";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { Row, Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { OrgIdContext, Domain, ClientTypes } from "../../store/orgId_context";
import ButtonLink from "../UI/ButtonLink";
import { useTranslation } from 'react-i18next';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CircleIcon from '@mui/icons-material/Circle';
import PanoramaFishEyeIcon from '@mui/icons-material/PanoramaFishEye';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Button from "../UI/Button";
import { NotificationManager} from 'react-notifications';
import { Link, Navigate, NavLink, useNavigate } from "react-router-dom";
import gwhale from "guidewhale";

const apiRequestAddress = process.env.REACT_APP_API_ENDPOINT || window.location.origin + "/json.rpc";
const e2Data = new E2Data("EU_AOrg_Packages");
e2Data.format = "Full";
const e2OrgData = new E2Data("EU_AOrg");
/* const e2DevicesData = new E2Data("VP_DevicesOrgUnits");
e2DevicesData.format = "Full";
e2DevicesData.filter = 'AssignedTo="CKT" AND AddByReg=true'; */
const isNullOrEmpty = (value) => {
    return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
}

const Todo = ({clients, max="200px"}) => {
    const {sessionInfo, domain, clientType, mainOrgId, aboutOrgId} = useContext(OrgIdContext);
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const [resending, setResending] = useState(false);
    const [hasClients, setHasClients] = useState(false);
    const [missingKeys, setMissingKeys] = useState(false);
    const [e2DevicesData] = useState(new E2Data("VP_DevicesOrgUnits").ApplyParams({format:"Full", filter: `AssignedTo="CKT" AND AddByReg=true AND OrgId='${aboutOrgId}'`}))
    const [e2ServiceData] = useState(new E2Data("EU_AOrg_ServicesEndpoints").ApplyParams({format:"Full", filter: `ServiceEndPointCode__Step="40"`}));
    const [hasFisc, setHasFisc] = useState(false);
    const [fiscMissing, setFiscMissing]= useState(false);
    const navigate = useNavigate();

    const {isPending : isOrgPending, data : orgData, refetch} = useQuery({
        queryKey: ['Data.GetEntityData', e2OrgData.GetParamsObject()],
        queryFn: () => e2OrgData.GetData(),
    })

    const {isPending : devicesDataPending, data : devicesData, refetch2} = useQuery({
        queryKey: ['Data.GetEntityData', e2DevicesData.GetParamsObject()],
        queryFn: () => e2DevicesData.GetData(),
        enabled: ["SI", "ME"].indexOf(sessionInfo?.Extended?.CountryCode) > -1
    })

    const {isPending : serviceDataPending, data : serviceData, refetch3} = useQuery({
        queryKey: ['Data.GetEntityData', e2ServiceData.GetParamsObject()],
        queryFn: () => e2ServiceData.GetData(),
        enabled: ["SI", "ME"].indexOf(sessionInfo?.Extended?.CountryCode) > -1
    })

    const resendConfirmMail = () =>{
        const ddd = new DDD(apiRequestAddress);
        setResending(true);
        ddd.Rpc_ExecuteMethod("Auth.SendUserPropertyConfirmationEmail", {"property": "email"}).then(() =>{
            setResending(false);
            NotificationManager.success(``, t("E-mail sent"));
        })
    }

    useEffect(()=>{
        if(!clients) {setGWhaleClients(false);return;}
        setHasClients(clients.some(i => i.CustomerId !== mainOrgId))
        setGWhaleClients(clients.some(i => i.CustomerId !== mainOrgId));
    },[clients,mainOrgId]);

    const hasPackage = () =>{
        const packages = sessionInfo?.Extended?.CurrentPackages;
        if(!packages) return false;
        if(!packages?.REI_Package?.Trial) return true;
        if(!packages?.IEI_Package?.Trial) return true;
        if(!packages?.FISC_Package?.Trial) return true;
        return false;
    }

    useEffect(() =>{
        if(!orgData){setGWhaleCompanyInfo(null); return setMissingKeys(false);}
        const prof = orgData.result?.data[0];
        if(!prof) {setGWhaleCompanyInfo(null); return setMissingKeys(false);}
        const _data = {
            Name : t("Name"),
            Address : t("Address"),
            PostCode : t("Postal Code"),
            CityName : t("City Name"),
            CountryCode : t("Country Code"),
            TaxNum : t("Tax Number"),
            ConPersonEmail : t("Contact Email"),
            ConPersonTelephone : t("Phone Number"),
        }
        let missing = "";
        Object.keys(_data).forEach(key => {
            if(isNullOrEmpty(prof[key])){
                missing += _data[key] + ", ";
            }
        })
        if(missing.length ===0) return setMissingKeys(false);

        missing = missing.slice(0, -2);

        setGWhaleCompanyInfo(missing);
        setMissingKeys(t("Missing information: "+missing))
    },[orgData]);

    useEffect(() => {
        if(clientType === ClientTypes.ISV) return;
        if(!devicesData?.result?.data?.[0]) return;
        if(!serviceData?.result?.data?.[0]) return;
        if(sessionInfo?.Extended?.CountryCode === "SI" && sessionInfo?.Extended?.CurrentPackages?.FISC_Package?.Trial === true) return setHasFisc(false);
        setHasFisc(true)
        let missing = "";
        const _service = serviceData?.result?.data?.[0];
        const _device = devicesData?.result?.data?.[0];
        if(isNullOrEmpty(_service.Certificate))
            missing = t("Certificate") + ", ";
        if(isNullOrEmpty(_service.CertPass))
            missing += t("Certificate password") + ", ";
        if(isNullOrEmpty(_device.FiscDeviceRegCode))
            missing += t("Device register code") + ", ";
        if(isNullOrEmpty(_device.FiscUnitRegCodeDef) && isNullOrEmpty(_device.OrgUnitId))
            missing += t("Unit register code") + ", ";
        if(isNullOrEmpty(_device.FiscOperatorRegCode) && isNullOrEmpty(_device.FiscOperatorRegCodeDef))
            missing += t("Operator register code") + ", ";
        if(missing.length ===0) return setFiscMissing(false);
        missing = missing.slice(0, -2);
        setFiscMissing(t("Missing information: "+missing))
    }, [devicesData, sessionInfo,serviceData, clientType]);

    const trySetCompanyInfo = (message) => {
  if (!window.gwhale || typeof window.gwhale.setCompanyProperties !== "function") {
    return false;
  }

  window.gwhale.setCompanyProperties({
    missingProfileInformation: !!message,
    missingProfileText: message,
  });

  return true;
};

const trySetClients = (cl) => {
  if (!window.gwhale || typeof window.gwhale.setCompanyProperties !== "function") {
    return false;
  }

  window.gwhale.setCompanyProperties({
    clients: cl,
  });

  return true;
};

const setGWhaleCompanyInfo = (message) => {
    /*if(!gwhale || !gwhale.initialized) return;
    window.gwhale.setCompanyProperties({
        missingProfileInformation: !!message,
        missingProfileText: message,
      });*/
  /* const update = () => {
    if (window.gwhale && typeof window.gwhale.setCompanyProperties === "function") {
      window.gwhale.setCompanyProperties({
        missingProfileInformation: !!message,
        missingProfileText: message,
      });
    }
  };

  window.addEventListener(
      "gwhale-content-updated",
      function handler() {
        console.log(11111111111111111111);
        update();
        window.removeEventListener("gwhale-content-updated", handler);
      },
      { once: true }
    ); */

  /* if (window.gwhale && typeof window.gwhale.setCompanyProperties === "function") {
    // Already ready, run immediately
    update();
  } else {
    // Wait for SDK to finish initializing
    window.addEventListener(
      "gwhale-content-updated",
      function handler() {
        update();
        window.removeEventListener("gwhale-content-updated", handler);
      },
      { once: true }
    );
  } */
};

const setGWhaleClients = (cl) => {
  /*if (trySetClients(cl)) return;

  setTimeout(() => {
    trySetClients(cl);
  }, 1000);*/
};


    return (<>
    <div className={classes.container} style={{textAlign:"center", maxHeight:max}}>
        <Row>
            <Col>
                <div className="d-flex justify-content-center flex-nowrap">
                    <h4 className={`mb-3 ${classes.subtitle}`} style={{fontSize:"0.9rem"}}>{t("TO-DO")}</h4>
                    {/* {tooltip && <OverlayTrigger placement="top" overlay={<Tooltip>{tooltip}</Tooltip>}>
                        <HelpOutlineIcon style={{marginLeft:"0.5rem", fontSize:"1rem"}}/>
                    </OverlayTrigger>} */}
                </div>
            </Col>
        </Row>
        <Row>
            <Col>
                <div className="d-flex " onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} style={{overflow: "hidden" }}>
                    <div><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Verify email")}</div>
                    <div className="ms-auto d-flex align-items-center">
                        {sessionInfo?.User?.AccessTiers?.MailVerified && <CheckCircleIcon style={{color:"green"}}/>}
                        {!sessionInfo?.User?.AccessTiers?.MailVerified && <>
                            <PanoramaFishEyeIcon/>
                            {isHovered && <Button className="ms-2" onClick={resendConfirmMail} disabled={resending}>{t("Resend")}</Button>}
                        </>}
                    </div>
                </div>
                <div className="d-flex">
                    {missingKeys && 
                    <OverlayTrigger placement="top" overlay={<Tooltip>{missingKeys}</Tooltip>}>
                        <div className={missingKeys ? classes["todohover"] : undefined} onClick={() => {navigate("/my-profile")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Profile information")}</div>
                    </OverlayTrigger>}
                    {!missingKeys && <div className={missingKeys ? classes["todohover"] : undefined} onClick={() => {navigate("/my-profile")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Profile information")}</div>}
                    <div className="ms-auto">
                        {!missingKeys  && <CheckCircleIcon style={{color:"green"}}/>}
                        {missingKeys  && <PanoramaFishEyeIcon/>}
                    </div>
                </div>
                {hasFisc &&
                <div className="d-flex">
                    {fiscMissing && 
                    <OverlayTrigger placement="top" overlay={<Tooltip>{fiscMissing}</Tooltip>}>
                        <div className={fiscMissing ? classes["todohover"] : undefined} onClick={() => {navigate("/api-keys-and-credentials")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Fiscalization information")}</div>
                    </OverlayTrigger>}
                    {!fiscMissing && <div className={fiscMissing ? classes["todohover"] : undefined} onClick={() => {navigate("/api-keys-and-credentials")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Fiscalization information")}</div>}
                    <div className="ms-auto">
                        {!fiscMissing  && <CheckCircleIcon style={{color:"green"}}/>}
                        {fiscMissing  && <PanoramaFishEyeIcon/>}
                    </div>
                </div>
                }
                {(clientType === ClientTypes.Company || clientType === ClientTypes.ISV) && 
                <div className="d-flex">
                    <div className={!hasPackage() ? classes["todohover"] : undefined} onClick={() => {navigate("/change-payment-plan")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Select a package")}</div>
                    <div className="ms-auto">
                        {hasPackage()  && <CheckCircleIcon style={{color:"green"}}/>}
                        {!hasPackage()  && <PanoramaFishEyeIcon/>}
                    </div>
                </div>}
                {(clientType === ClientTypes.Company || clientType===ClientTypes.EndClient) && <div className="d-flex">
                    <div className={(sessionInfo?.Extended?.ModeREI!=="PROD" && sessionInfo?.Extended?.ModeIEI!=="PROD") ? classes["todohover"] : undefined} onClick={() => {navigate("/operation-mode")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Move to production")}</div>
                    <div className="ms-auto">
                        {(sessionInfo?.Extended?.ModeREI==="PROD" || sessionInfo?.Extended?.ModeIEI==="PROD")  && <CheckCircleIcon style={{color:"green"}}/>}
                        {(sessionInfo?.Extended?.ModeREI!=="PROD" && sessionInfo?.Extended?.ModeIEI!=="PROD")  && <PanoramaFishEyeIcon/>}
                    </div>
                </div>}
                {(clientType === ClientTypes.ISV || clientType===ClientTypes.AccService) && <div className="d-flex">
                    <div className={!hasClients ? classes["todohover"] : undefined} onClick={() => {navigate("/clients")}}><CircleIcon style={{fontSize:"0.7rem", marginRight:"0.5rem"}}/>{t("Create first customer")}</div>
                    <div className="ms-auto">
                        {hasClients && <CheckCircleIcon style={{color:"green"}}/>}
                        {!hasClients  && <PanoramaFishEyeIcon/>}
                    </div>
                </div>}
            </Col>
        </Row>
    </div>
    </>);
}

export default Todo;